import React, { useEffect, useState } from 'react';
import { Check, Copy, Loader2 } from 'lucide-react';
import { createAgencyEmbed, fetchMyAgency, type MyAgency } from '@/services/siteApi';

// What an agency account gets on /my-site that an actor doesn't: the Hey Joe
// Messages embed (the Talent Messages panel from casting, as an iframe) and the
// roster as links, so both can go straight into whatever builds their site.
// Renders nothing for everyone else.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 24 };
const h2: React.CSSProperties = { margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em', color: INK };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const quiet: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 36, padding: '6px 12px', borderRadius: 10, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, background: '#fff', color: INK, border: '1px solid var(--hj-hairline)', cursor: 'pointer', flexShrink: 0 };
const primary: React.CSSProperties = { ...quiet, background: INK, color: '#fff', border: '1px solid ' + INK };
const pre: React.CSSProperties = { background: 'var(--hj-surface-1)', borderRadius: 10, padding: 12, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre', margin: 0 };

const CASTING_SETTINGS = 'https://casting.heyjoe.io/?tab=settings';

export const rosterText = (a: MyAgency) => a.roster.map((t) => `${t.name}: ${t.headshot || 'no headshot yet'}`).join('\n');
// A plain roster grid for a site builder: every name with its headshot. The links
// are permanent (casting re-signs them on each visit), so the page keeps working.
export function rosterHtml(a: MyAgency) {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  const cards = a.roster.map((t) => `  <figure>\n    ${t.headshot ? `<img src="${esc(t.headshot)}" alt="${esc(t.name)}" loading="lazy">` : '<div class="hj-noshot"></div>'}\n    <figcaption>${esc(t.name)}</figcaption>\n  </figure>`).join('\n');
  return `<style>\n.hj-roster{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:20px;margin:0;padding:0}\n.hj-roster figure{margin:0;text-align:center}\n.hj-roster img,.hj-roster .hj-noshot{width:100%;aspect-ratio:4/5;object-fit:cover;border-radius:10px;background:#eee;display:block}\n.hj-roster figcaption{margin-top:8px;font-weight:600}\n</style>\n<section class="hj-roster">\n${cards}\n</section>`;
}

const CopyBtn: React.FC<{ text: string; label?: string; aria: string; style?: React.CSSProperties }> = ({ text, label = 'Copy', aria, style }) => {
  const [done, setDone] = useState(false);
  return (
    <button type="button" aria-label={aria} style={{ ...quiet, ...style }} onClick={() => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1800); }).catch(() => {}); }}>
      {done ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}{done ? 'Copied' : label}
    </button>
  );
};

const SiteAgencyPanel: React.FC<{ onLoaded?: (a: MyAgency) => void; onSnippet?: (s: string) => void }> = ({ onLoaded, onSnippet }) => {
  const [agency, setAgency] = useState<MyAgency | null>(null);
  const [snippet, setSnippet] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { fetchMyAgency().then((a) => { if (a.agency) { setAgency(a); onLoaded?.(a); } }).catch(() => setAgency(null)); }, []);
  if (!agency) return null;
  const generate = async () => {
    setBusy(true); setErr(null);
    try { const r = await createAgencyEmbed(); setSnippet(r.snippet); onSnippet?.(r.snippet); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not make the embed code.'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
        <h2 style={h2}>Hey Joe Messages on your site</h2>
        <p style={small}>Your Talent Messages panel from Hey Joe Casting, inside your own website. Talent message you there; you answer from Hey Joe. Paste the code where you want it on the page.</p>
        {!agency.isPlus ? (
          <p style={small}>This comes with Hey Joe Plus for agencies. Once Plus is on your agency’s account, the code appears here.</p>
        ) : snippet ? (
          <>
            <pre style={pre}>{snippet}</pre>
            <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
              <CopyBtn text={snippet} label="Copy the code" aria="Copy the embed code" style={primary} />
              <a href={CASTING_SETTINGS} target="_blank" rel="noreferrer" style={{ ...small, color: INK }}>Rotate or turn it off in casting settings</a>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
            <button type="button" disabled={busy} style={primary} onClick={generate}>{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> One moment</> : 'Get the embed code'}</button>
            <span style={small}>Same code as casting’s Settings tab.</span>
          </div>
        )}
        {err && <p style={{ ...small, color: 'var(--hj-pink-ink)' }}>{err}</p>}
      </section>

      {agency.roster.length > 0 && (
        <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
          <div className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
            <h2 style={h2}>Your roster</h2>
            <div className="flex" style={{ gap: 8 }}>
              <CopyBtn text={rosterText(agency)} label="Copy as links" aria="Copy every roster link" />
              <CopyBtn text={rosterHtml(agency)} label="Copy as HTML" aria="Copy the roster as HTML" />
            </div>
          </div>
          <p style={small}>{agency.roster.length} on your Hey Joe roster, with headshot links that keep working. Paste the links into whatever builds your site, or the HTML straight onto a page.</p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 360, overflowY: 'auto' }}>
            {agency.roster.map((t) => (
              <li key={t.id} className="flex items-center" style={{ gap: 12, padding: '8px 0', borderTop: '1px solid var(--hj-hairline-soft)' }}>
                {t.headshot
                  ? <img src={t.headshot} alt="" loading="lazy" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, background: 'var(--hj-surface-2)', flexShrink: 0 }} />
                  : <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--hj-surface-2)', flexShrink: 0 }} />}
                <span style={{ fontWeight: 600, fontSize: 15, color: INK, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                {t.headshot && <CopyBtn text={t.headshot} aria={`Copy the headshot link for ${t.name}`} />}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
};

export default SiteAgencyPanel;
